import { FC, FunctionComponent, ReactNode, useMemo, useState } from 'react';
import { Checkbox, Collapse, Form, FormInstance, Tag } from 'antd';
import { Rule } from 'antd/es/form';
import { FormBuilderData, FormBuilderFieldBase, FormBuilderFormData, FormBuilderVisibleCondition } from 'types/formBuilder';
import { UploadFileApi } from 'types/upload-type';
import FieldHeader from './fields/FieldHeader';
import FieldTextArea from './fields/FieldTextArea';
import FieldInput from './fields/FieldInput';
import FieldCheckbox from './fields/FieldCheckbox';
import FieldDate from './fields/FieldDate';
import FieldSelect from './fields/FieldSelect';
import FieldUpload from './fields/FieldUpload';
import FieldRadio from './fields/FieldRadio';
import FormItem from './FormItem';
import FieldTextEditor from './fields/FieldTextEditor';

export function isFieldVisible(visible: FormBuilderVisibleCondition, fieldValue: any) {
  if (visible.values === undefined) {
    return Array.isArray(fieldValue) ? fieldValue.length > 0 : !!fieldValue;
  }

  if (Array.isArray(fieldValue)) {
    if (Array.isArray(visible.values)) {
      if (visible.operator === 'and') {
        return visible.values.filter((x) => !fieldValue.includes(x)).length === 0;
      }

      return visible.values.find((x) => fieldValue.includes(x)) !== undefined;
    }

    return visible.values === null ? fieldValue.length === 0 : fieldValue.includes(visible.values);
  }

  if (Array.isArray(visible.values)) {
    return visible.values.includes(fieldValue);
  }

  return visible.values === null ? fieldValue === undefined || fieldValue === null : fieldValue === visible.values;
}

const inputComponents: Record<
  FormBuilderFormData['type'],
  FunctionComponent<{
    data: any;
    rules?: Rule[];
    form: FormInstance;
    disabled: boolean;
    onTextImageUpload?: (upload: UploadFileApi) => Promise<void | string>;
    forceSingleColumn?: boolean;
  }>
> = {
  date: FieldDate,
  checkbox: FieldCheckbox,
  radio: FieldRadio,
  select: FieldSelect,
  input: FieldInput,
  textArea: FieldTextArea,
  textEditor: FieldTextEditor,
  upload: FieldUpload,
  header: FieldHeader,
  formGroup: FieldInput,
};

interface FormBuilderAnswersVisibleControlProps {
  formValues: any;
  inputData?: any;
  data?: any;
  visible: FormBuilderVisibleCondition;
  children: ReactNode;
}

const FormBuilderAnswersVisibleControl: FC<FormBuilderAnswersVisibleControlProps> = ({
  formValues,
  data = [],
  visible,
  children,
}) => {
  let fieldValue = formValues?.[visible.field];

  if (fieldValue === undefined) {
    const fieldFromData = data.find((item: any) => item.name === visible.field);
    if (fieldFromData) {
      fieldValue = fieldFromData.answer;
    }
  }

  const isVisible = useMemo(() => isFieldVisible(visible, fieldValue), [visible, fieldValue]);

  return isVisible ? <>{children}</> : null;
};

interface FormBuilderAnswersInputsProps {
  data: FormBuilderFormData[];
  form: FormInstance;
  formValues: any;
  listData?: FormBuilderData[];
  disabled: boolean;
  showFormGroupsAsAccordion?: boolean;
  onTextImageUpload?: (upload: UploadFileApi) => Promise<void | string>;
  renderField?: (inputData: FormBuilderFormData, form: FormInstance, disabled: boolean) => ReactNode;
}

const FormBuilderAnswersInputs: FC<FormBuilderAnswersInputsProps> = ({
  data,
  form,
  formValues,
  listData,
  disabled,
  onTextImageUpload,
  renderField,
  showFormGroupsAsAccordion = false,
}) => {
  const [activeKeys, setActiveKeys] = useState<string[]>([]);

  const panelKeys = useMemo(() => {
    return data
      .filter((inputData) => inputData.type !== 'formGroup' && !inputData.visible)
      .map((inputData) => (inputData as FormBuilderFieldBase).name);
  }, [data]);

  const [isExpanded, setIsExpanded] = useState(false);

  const currentActiveKeys = useMemo(() => {
    return isExpanded ? panelKeys : activeKeys;
  }, [isExpanded, panelKeys, activeKeys]);

  if (showFormGroupsAsAccordion) {
    return (
      <>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Tag
            color="blue"
            onClick={() => {
              setIsExpanded((prev) => !prev);
              if (!isExpanded) {
                setActiveKeys(panelKeys);
              } else {
                setActiveKeys([]);
              }
            }}
            style={{ cursor: 'pointer', margin: '0 0 10px 0' }}
          >
            {isExpanded ? 'Collapse all' : 'Expand all'}
          </Tag>
        </div>
        <Collapse
          activeKey={currentActiveKeys}
          onChange={(keys: any) => {
            setActiveKeys(keys as string[]);

            if (keys.length === panelKeys.length && panelKeys.length > 0) {
              setIsExpanded(true);
            } else {
              setIsExpanded(false);
            }
          }}
        >
          {data.map((inputData, index) => {
            const hasMoreThanOneStep = data.length > 1;

            if (inputData.type === 'formGroup') {
              const refForm = inputData.fields ?? listData?.find((item) => item.id === inputData.formId)?.form;

              if (!refForm) {
                return undefined;
              }

              const refFormWithoutProjectBrief = refForm.filter((item) => {
                return !(item.type === 'textEditor' && item.name === 'project_brief');
              });

              return inputData.visible ? (
                <FormBuilderAnswersVisibleControl
                  data={data}
                  key={inputData.formId}
                  formValues={formValues}
                  visible={inputData.visible}
                >
                  <FormBuilderAnswersInputs
                    key={inputData.formId}
                    data={refFormWithoutProjectBrief}
                    form={form}
                    formValues={formValues}
                    listData={listData}
                    disabled={disabled}
                    onTextImageUpload={onTextImageUpload}
                    renderField={renderField}
                  />
                </FormBuilderAnswersVisibleControl>
              ) : (
                <FormBuilderAnswersInputs
                  key={inputData.formId}
                  data={refFormWithoutProjectBrief}
                  form={form}
                  formValues={formValues}
                  listData={listData}
                  disabled={disabled}
                  onTextImageUpload={onTextImageUpload}
                  renderField={renderField}
                />
              );
            }

            const Comp = inputComponents[inputData.type];
            if (!Comp) return null;

            let hasStatusLock = false;
            let isFullyAnswered = false;

            if (inputData.type === 'checkbox') {
              hasStatusLock = !!inputData.statusLock?.length;
              isFullyAnswered = inputData.options.length === (formValues[inputData.name]?.length ?? 0);
            }

            return (
              !inputData.visible && (
                <Collapse.Panel
                  header={hasMoreThanOneStep ? `Step ${index + 1}: ${inputData.label}` : inputData.label}
                  key={inputData.name}
                  className={hasStatusLock && !isFullyAnswered ? ' collapse-status-lock' : ''}
                >
                  <FormItem data={inputData} showInAccordion>
                    <Comp data={inputData} form={form} disabled={disabled} onTextImageUpload={onTextImageUpload} />
                  </FormItem>
                </Collapse.Panel>
              )
            );
          })}
        </Collapse>
      </>
    );
  }

  return (
    <>
      {data.map((inputData) => {
        if (inputData.type === 'formGroup') {
          const refForm = inputData.fields ?? listData?.find((item) => item.id === inputData.formId)?.form;

          if (!refForm) {
            return undefined;
          }

          const refFormWithoutProjectBrief = refForm.filter((item) => {
            return !(item.type === 'textEditor' && item.name === 'project_brief');
          });

          return inputData.visible ? (
            <FormBuilderAnswersVisibleControl
              data={data}
              key={inputData.formId}
              formValues={formValues}
              visible={inputData.visible}
            >
              <FormBuilderAnswersInputs
                key={inputData.formId}
                data={refFormWithoutProjectBrief}
                form={form}
                formValues={formValues}
                listData={listData}
                disabled={disabled}
                onTextImageUpload={onTextImageUpload}
                renderField={renderField}
              />
            </FormBuilderAnswersVisibleControl>
          ) : (
            <FormBuilderAnswersInputs
              key={inputData.formId}
              data={refFormWithoutProjectBrief}
              form={form}
              formValues={formValues}
              listData={listData}
              disabled={disabled}
              onTextImageUpload={onTextImageUpload}
              renderField={renderField}
            />
          );
        }

        const Comp = inputComponents[inputData.type];

        if (Comp) {
          return inputData.visible ? (
            <FormBuilderAnswersVisibleControl
              key={inputData.name}
              inputData={inputData}
              data={data}
              formValues={formValues}
              visible={inputData.visible}
            >
              <FormItem data={inputData}>
                <Comp data={inputData} form={form} disabled={disabled} onTextImageUpload={onTextImageUpload} />
              </FormItem>
            </FormBuilderAnswersVisibleControl>
          ) : (
            <FormItem key={inputData.name} data={inputData}>
              <Comp data={inputData} form={form} disabled={disabled} onTextImageUpload={onTextImageUpload} />
            </FormItem>
          );
        }

        return undefined;
      })}
    </>
  );
};

interface FormBuilderAnswersProps {
  data: FormBuilderFormData[];
  form: FormInstance;
  formValues?: any;
  listData?: FormBuilderData[];
  disabled?: boolean;
  showFormGroupsAsAccordion?: boolean;
  onTextImageUpload?: (upload: UploadFileApi) => Promise<void | string>;
  renderField?: (inputData: FormBuilderFormData, form: FormInstance, disabled: boolean) => ReactNode;
}

const FormBuilderAnswers: FC<FormBuilderAnswersProps> = ({
  data,
  form,
  formValues = {},
  listData,
  disabled = false,
  onTextImageUpload,
  showFormGroupsAsAccordion = false,
}) => {
  return (
    <div style={{ overflow: 'hidden' }}>
      <Form.Item name="submitting" valuePropName="checked" hidden>
        <Checkbox />
      </Form.Item>

      <Form.Item name="alert_submitting" valuePropName="checked" hidden>
        <Checkbox />
      </Form.Item>

      <FormBuilderAnswersInputs
        data={data}
        listData={listData}
        form={form}
        formValues={formValues}
        disabled={disabled}
        showFormGroupsAsAccordion={showFormGroupsAsAccordion}
        onTextImageUpload={onTextImageUpload}
      />
    </div>
  );
};

export default FormBuilderAnswers;
